import type { SyncDelta } from '@couplespace/sync';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import { clearAllPageDocuments, invalidatePageDocumentsTouching } from '../blocks/page-cache';
import { subscribeLiveDeltas, subscribeLiveReconnect } from '../live/connection';
import { routeTouchesPage } from '../live/route-delta';

const LOCAL_SAVE_SUPPRESS_MS = 5_000;

export type PageEditorHandle = {
  isDirty: () => boolean;
  reload: () => void;
  /** Ids of the page and every block currently in the editor. */
  blockIds?: () => ReadonlySet<string>;
  /** Patch remote block changes in place; returns false when a full reload is needed. */
  applyRemote?: (deltas: SyncDelta[]) => boolean;
};

type WebSyncContextValue = {
  registerPageEditor: (pageId: string, handle: PageEditorHandle) => () => void;
  notifyLocalSave: (pageId: string) => void;
};

const WebSyncContext = createContext<WebSyncContextValue | null>(null);

export function WebSyncProvider({ children }: { children: ReactNode }) {
  const editorsRef = useRef(new Map<string, PageEditorHandle>());
  const localSaveSuppressUntilRef = useRef(new Map<string, number>());

  const registerPageEditor = useCallback((pageId: string, handle: PageEditorHandle) => {
    editorsRef.current.set(pageId, handle);
    return () => {
      editorsRef.current.delete(pageId);
      localSaveSuppressUntilRef.current.delete(pageId);
    };
  }, []);

  const notifyLocalSave = useCallback((pageId: string) => {
    localSaveSuppressUntilRef.current.set(pageId, Date.now() + LOCAL_SAVE_SUPPRESS_MS);
  }, []);

  useEffect(() => {
    const reloadIfClean = (pageId: string, handle: PageEditorHandle, now: number) => {
      const suppressUntil = localSaveSuppressUntilRef.current.get(pageId) ?? 0;
      if (now < suppressUntil || handle.isDirty()) {
        return;
      }
      handle.reload();
    };

    const unsubscribeDeltas = subscribeLiveDeltas((route, deltas) => {
      if (route.refetchAll) {
        clearAllPageDocuments();
      } else {
        invalidatePageDocumentsTouching(route.blockIds);
      }

      const now = Date.now();
      const blockDeltas = deltas.filter((delta) => delta.table === 'blocks');
      for (const [pageId, handle] of editorsRef.current) {
        const pageBlockIds = handle.blockIds?.() ?? new Set([pageId]);
        if (!routeTouchesPage(route, pageBlockIds)) {
          continue;
        }
        if (!route.refetchAll && handle.applyRemote?.(blockDeltas)) {
          continue;
        }
        reloadIfClean(pageId, handle, now);
      }
    });

    const unsubscribeReconnect = subscribeLiveReconnect(() => {
      clearAllPageDocuments();
      const now = Date.now();
      for (const [pageId, handle] of editorsRef.current) {
        reloadIfClean(pageId, handle, now);
      }
    });

    return () => {
      unsubscribeDeltas();
      unsubscribeReconnect();
    };
  }, []);

  const value = useMemo(
    () => ({
      registerPageEditor,
      notifyLocalSave,
    }),
    [registerPageEditor, notifyLocalSave],
  );

  return <WebSyncContext.Provider value={value}>{children}</WebSyncContext.Provider>;
}

export function useWebSync(): WebSyncContextValue {
  const ctx = useContext(WebSyncContext);
  if (!ctx) {
    throw new Error('useWebSync must be used within WebSyncProvider');
  }
  return ctx;
}

export function useWebSyncPage(pageId: string): {
  registerEditor: (handle: PageEditorHandle) => () => void;
  notifyLocalSave: () => void;
} {
  const { registerPageEditor, notifyLocalSave } = useWebSync();

  const registerEditor = useCallback(
    (handle: PageEditorHandle) => registerPageEditor(pageId, handle),
    [pageId, registerPageEditor],
  );

  const notifyPageLocalSave = useCallback(
    () => notifyLocalSave(pageId),
    [pageId, notifyLocalSave],
  );

  return { registerEditor, notifyLocalSave: notifyPageLocalSave };
}
